/**
 * src/lib/api/leads.js - Lead sync client API
 * Talks to /api/data (POST) -- manual trigger for the Gmail enquiry sync.
 * The server gates this to ceo/admin and returns the structured summary.
 */

import { apiFetch, API_BASE } from './client';
import { makeEntityCache, reconcileCollection } from '../offline/entityCache';
import { getOnline } from '../offline/networkStatus';

const DATA = `${API_BASE}/data`;

// Per-deal email cache key so each deal keeps its own offline copy of the
// History-tab email thread (mirrors crm.js activitiesCache). Best-effort — a
// cache miss/error never degrades the live result.

// Trigger a Gmail lead-sync run. `daysBack` narrows the inbox lookback window;
// `dryRun` previews (no acknowledgement sent, no rows created). Returns the
// core's structured result: { ok, created, skipped, replied, errors, summary }.
export async function syncGmailLeads({ daysBack, dryRun } = {}) {
  return apiFetch(DATA, {
    method: 'POST',
    body: JSON.stringify({
      action: 'leads.sync_gmail',
      days_back: daysBack,
      dry_run: dryRun,
    }),
    // Gmail scan + per-email lead classification is slow and scales with new-email
    // volume — it exceeds the 15s apiFetch default when there are enquiries to process,
    // so the button showed "lead sync failed" (a client-side abort at 15s) even while the
    // server was still working (api/data.js maxDuration is 60s). Wait for the server's
    // real verdict; mirrors ingestEmailAttachments. Telemetry 2026-07-02:
    // data.leads.sync_gmail timed out client-side at 15s (succeeded in ~3s earlier when
    // the inbox was empty).
    timeout: 120000,
  });
}

// Compose & send a free-form email from the app (TICKET-206 / TICKET-email-photo-attachments).
// The server gates this to ceo/admin and routes through the SAME Gmail mailbox as the lead sync.
// Optional attachment_ids[] (project_documents ids) are resolved server-side → MIME attachments.
// Returns the structured result: { ok, sent, to, sent_id } or { ok:false, error }.
export async function sendGmailEmail({ to, subject, body, attachment_ids } = {}) {
  return apiFetch(DATA, {
    method: 'POST',
    body: JSON.stringify({
      action: 'leads.send_email',
      to,
      subject,
      body,
      ...(Array.isArray(attachment_ids) && attachment_ids.length ? { attachment_ids } : {}),
    }),
  });
}

// S4: pull a deal/project's email attachments (Gmail PDF/spec -> project_documents ->
// DocParser text -> brain) so they become quote/invoice context. Deal-scoped (no inbox
// re-scan), idempotent, degrades gracefully if Gmail is unconfigured. DocParser + brain
// are slow -> generous timeout. Returns { success, filed, deduped, errors, document_ids }
// | { success:false, unconfigured:true, error }.
export async function ingestEmailAttachments({ deal_id, project_id } = {}) {
  return apiFetch(DATA, {
    method: 'POST',
    body: JSON.stringify({ action: 'leads.ingest_attachments', deal_id, project_id }),
    timeout: 120000,
  });
}

// -- TICKET-207: deal email timeline ------------------------------------------

// Fetch the email thread for a deal (inbound + outbound lead_emails rows).
// Returns { ok, thread_id, emails: [{id, direction, from_email, from_name,
//   to_email, subject, body, snippet, sent_at, created_at}] }.
export async function fetchLeadThread(dealId) {
  if (!dealId) return { ok: false, emails: [] };
  // TICKET-265: cache the email thread per deal so the History tab renders
  // offline. Mirrors fetchCRMActivities — reconcile writes the cache online,
  // hydrates it offline, and the stale-empty guard keeps a good cache from
  // being blanked by a transient empty online fetch.
  const online = getOnline();
  const key = 'leadEmails::' + dealId;
  try {
    const r = await apiFetch(DATA, {
      method: 'POST',
      body: JSON.stringify({ action: 'leads.thread', deal_id: dealId }),
    });
    // TICKET-609: apiFetch returns { success: false, status, error } on POST failures
    // (403/401 etc). Must NOT serve cached data on a permission denial — branch
    // on success BEFORE calling reconcileCollection, which treats empty fetch as
    // a "stale, use cache" signal (needed for transient network blips but not for
    // permission denials). Failure must be transparent to caller.
    if (r && r.success === false) {
      return { ok: false, ...r, emails: [] };
    }
    const fetched = Array.isArray(r?.emails) ? r.emails : [];
    // reconcileCollection is internally best-effort (returns fetched on any cache
    // error) — writes the cache online, hydrates it offline, and keeps a good
    // cache from being blanked by a transient empty online fetch.
    const rows = await reconcileCollection(key, fetched, online);
    return { ...(r || {}), ok: r?.ok ?? true, emails: rows };
  } catch (e) {
    // spine-emit-ok: offline/network read-only fallback — hydrating the last-known
    // thread from cache IS the expected offline path (mirrors fetchCRMActivities),
    // not an error to log; the live fetch retries on reconnect. entityCache.read()
    // is itself guarded (returns [] on error), so no silent catch here.
    const cached = await makeEntityCache(key).read();
    return { ok: false, offline: true, emails: cached };
  }
}

// Draft an AI reply for the latest inbound email on a deal.
// Returns { ok, draft: { subject, body, language } }.
// TICKET-687: same client-abort class as syncGmailLeads (2026-07-02) — the
// server route does RAG grounding (12s budget) THEN an LLM generation call
// on top, routinely exceeding the apiFetch default 15s timeout. The client
// aborted silently before the server ever finished, so app_error_logs showed
// nothing and the button just looked broken. Match the sibling LLM-heavy
// actions' override.
// leadEmailId (TICKET-969): the inbound being answered; omitted = the deal's latest.
// previousBody/previousSubject (TICKET-972 S4): refine THIS text instead of drafting anew.
export async function draftLeadReply(dealId, { notes = '', language = '', leadEmailId = null, previousBody = '', previousSubject = '', instructionKind = null } = {}) {
  return apiFetch(DATA, {
    method: 'POST',
    body: JSON.stringify({
      action: 'leads.draft_reply', deal_id: dealId, notes, language,
      ...(leadEmailId ? { lead_email_id: leadEmailId } : {}),
      ...(previousBody ? { previous_body: previousBody, previous_subject: previousSubject || '' } : {}),
      ...(instructionKind ? { instruction_kind: instructionKind } : {}),
    }),
    timeout: 120000,
  });
}

// TICKET-975: the draft pre-generated on ingest, if still current ({ ok, draft|null, stale? }).
export async function getStoredReplyDraft(dealId, leadEmailId) {
  return apiFetch(DATA, { method: 'POST', body: JSON.stringify({ action: 'leads.get_draft', deal_id: dealId, lead_email_id: leadEmailId }) });
}

// TICKET-974: per-intent evidence for auto-send ({ ok, active, trust }).
export async function fetchReplyTrust() {
  return apiFetch(DATA, { method: 'POST', body: JSON.stringify({ action: 'leads.reply_trust' }) });
}

// Send a reply email for a deal and record it as an outbound lead_email row.
// Optional: attach_quote (boolean, default false) to include the deal's latest quote PDF.
//           attachment_ids (array of project_documents ids) to include saved documents.
// Returns { ok, sent_id, to } or { ok: false, error }.
export async function sendLeadReply({ deal_id, to, subject, body, attachment_ids, attach_quote, draft_id } = {}) {
  return apiFetch(DATA, {
    method: 'POST',
    body: JSON.stringify({
      action: 'leads.send_reply',
      deal_id,
      to,
      subject,
      body,
      ...(Array.isArray(attachment_ids) && attachment_ids.length ? { attachment_ids } : {}),
      ...(attach_quote ? { attach_quote: true } : {}),
      ...(draft_id ? { draft_id } : {}), // TICKET-973 reply audit ledger
    }),
  });
}

// -- TICKET-207: rejected leads review UI -------------------------------------

// List emails the AI filtered out as non-leads, pending human review.
// Returns { ok, rejections: [{ id, from_name, from_email, subject, snippet,
//   ai_reason, confidence, received_at }] }.
export async function listRejectedLeads() {
  return apiFetch(DATA, {
    method: 'POST',
    body: JSON.stringify({ action: 'leads.list_rejected' }),
  });
}

// Promote a filtered rejection back to a real lead (CEO overrides AI).
// Returns { ok } or { ok: false, error }.
export async function promoteRejection(rejectionId) {
  return apiFetch(DATA, {
    method: 'POST',
    body: JSON.stringify({ action: 'leads.promote_rejection', rejection_id: rejectionId }),
  });
}

// Dismiss a rejection (confirm AI was right -- never show it again).
// Returns { ok } or { ok: false, error }.
export async function dismissRejection(rejectionId) {
  return apiFetch(DATA, {
    method: 'POST',
    body: JSON.stringify({ action: 'leads.dismiss_rejection', rejection_id: rejectionId }),
  });
}
